import { createSlice } from '@reduxjs/toolkit';

import { NotificationLevel } from '@mfe/shared/schema-types';

export enum AlertAction {
  GoToBilling = 'GoToBilling',
  ScheduleInstall = 'ScheduleInstall',
  RescheduleServiceCall = 'RescheduleServiceCall',
  BuyMore = 'BuyMore',
  PlanUsage = 'PlanUsage',
  AutoPay = 'AutoPay',
  OneTimePayment = 'OneTimePayment',
  Refresh = 'Refresh',
  ResumeService = 'ResumeService',
  SignElectronicCustomerAgreement = 'SignElectronicCustomerAgreement',
}

export type AlertButton = {
  action?: AlertAction;
  url?: string;
  label: string;
  openInSameTab?: boolean;
};

export enum AlertType {
  ServiceCall = 'ServiceCall',
  AccountStatusError = 'AccountStatusError',
  AccountStatusTransitionError = 'AccountStatusTransitionError',
  Outage = 'Outage',
  PaymentMethodExpired = 'PaymentMethodExpired',
  NoValidPaymentMethod = 'NoValidPaymentMethod',
  PaymentMethodUpdated = 'PaymentMethodUpdated',
  PaymentRestoredService = 'PaymentRestoredService',
  PaymentOneTime = 'PaymentOneTime',
  PastDueInvoices = 'PastDueInvoices',
  BoletoRequested = 'BoletoRequested', // Brazil only
  SoftSuspend = 'SoftSuspend', // Brazil only
  PreSoftSuspend = 'PreSoftSuspend', // Brazil only
  SuspendedAccount = 'SuspendedAccount',
  AllDataUsed = 'AllDataUsed',
  AllDataUsedNextGen = 'AllDataUsedNextGen', // Probably no longer relevant
  ChangePlanSuccess = 'ChangePlanSuccess',
  CancelOrder = 'CancelOrder',
  CancelledAccount = 'CancelledAccount',
  DisconnectedAccount = 'DisconnectedAccount',
  DisconnectSuccess = 'DisconnectSuccess',
  RetentionOfferSuccess = 'RetentionOfferSuccess',
  UnscheduledInstallation = 'UnscheduledInstallation',
  ScheduledInstallation = 'ScheduledInstallation',
  UsageTier = 'UsageTier',
  ElectronicCustomerAgreement = 'ElectronicCustomerAgreement',
  NonServiceable = 'NonServiceable',
  OntModeSuccess = 'OntModeSuccess',
}

export enum DisplayLocation {
  LOCAL = 'local',
  GLOBAL = 'global',
}

export type Alert = {
  level: NotificationLevel;
  title?: string;
  caption?: string;
  button?: AlertButton;
  link?: AlertButton;
  customDescription?: string;
  type: AlertType;
  displayLocation: DisplayLocation;
};

export type AlertsState = {
  loading: boolean;
  alerts: Alert[];
  billingAlerts: Alert[];
};

export const initialAlertsState: AlertsState = {
  loading: true,
  alerts: [],
  billingAlerts: [],
};

const filterNewAlerts = (existingAlerts: Alert[], newAlerts: Alert[]): Alert[] => {
  return newAlerts.filter((alert) => {
    if (!alert) {
      return false;
    }
    return !existingAlerts.find((a) => a.title === alert.title);
  });
};

export const alertsSlice = createSlice({
  name: 'alertsState',
  initialState: initialAlertsState,
  reducers: {
    setAlerts: (state, { payload }: { payload: Alert[] }) => {
      const newAlerts = filterNewAlerts(state.alerts, payload);
      return {
        ...state,
        alerts: [...state.alerts, ...newAlerts],
        loading: false,
      };
    },
    setBillingAlerts: (state, { payload }: { payload: Alert[] }) => {
      const newAlerts = filterNewAlerts(state.billingAlerts, payload);
      return {
        ...state,
        billingAlerts: [...state.billingAlerts, ...newAlerts],
        loading: false,
      };
    },
    removeAlert: (state, { payload }: { payload: AlertType }) => ({
      ...state,
      alerts: state.alerts.filter((alert) => {
        if (!alert) {
          return;
        }

        return alert.type !== payload;
      }),
      billingAlerts: state.billingAlerts.filter((alert) => {
        if (!alert) {
          return;
        }

        return alert.type !== payload;
      }),
    }),
    resetAlerts: () => initialAlertsState,
    fetchAccountAlerts: (state) => state,
  },
});

export const { setBillingAlerts, setAlerts, removeAlert, resetAlerts, fetchAccountAlerts } = alertsSlice.actions;

export const selectAlerts = (state: { alertsState: AlertsState }) => state.alertsState;
